import { useToast } from "@ligo/layout-design-core";
import { Errors } from "classes";
import { allAttachmentsKey, ss } from "consts";
import { AttachmentModel } from "models";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import * as AttendanceService from "services/attendance";
import { aytyFormatError } from "utils";
import {
  AttBaseProviderType,
  PermissionAttProviderType,
  useAttendDetail,
} from "./detail";

type AttachmentContextType = {
  loading: boolean;
  attachments?: AttachmentModel[];
  getAttachments: (isReload?: boolean) => Promise<void>;
};

const AttachmentContext = createContext({} as AttachmentContextType);

const Provider = ({ all, children }: AttBaseProviderType) => {
  const [loading, setLoading] = useState(true);
  const [attachments, setAttachments] = useState<AttachmentModel[]>();
  const { pIdOmni, attendance } = useAttendDetail();
  const { error, warning } = useToast();
  const { t } = useTranslation();

  const errorsResolver = useMemo(
    () => new Errors({ error, warning }, t),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  useEffect(() => {
    if (pIdOmni) {
      setLoading(true);
      setAttachments(undefined);
    }
  }, [pIdOmni]);

  const getAttachments = useCallback(
    async (isReload?: boolean) => {
      let current: AttachmentModel[] = all[pIdOmni];

      if ((isReload || !current) && attendance)
        await AttendanceService.getAttachmentList({
          pIdMailing: attendance.IdMailing,
        })
          .then(async ({ data }) => {
            if (data.idReturnAPI > 0) {
              current = data.MailingAttachmentList;
              all[pIdOmni] = data.MailingAttachmentList;
            } else warning({ description: t(aytyFormatError(data)) });
          })
          .catch(errorsResolver.defaultError);

      setAttachments(current);
      ss.setItem(allAttachmentsKey, JSON.stringify(all));

      setLoading(false);
    },
    [all, pIdOmni, attendance, errorsResolver, warning, t],
  );

  return (
    <AttachmentContext.Provider
      value={{
        loading,
        attachments,
        getAttachments,
      }}
    >
      {children}
    </AttachmentContext.Provider>
  );
};

export const AttachmentProvider = ({
  hasPermission = true,
  ...args
}: PermissionAttProviderType) => {
  if (!hasPermission) return <>{args.children}</>;

  return <Provider {...args} />;
};

export const useAttachment = () => useContext(AttachmentContext);
